import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { signInAnonymously, onAuthStateChanged, getAuth } from 'firebase/auth';
import { collection, getDocs, query, limit } from 'firebase/firestore';

// Add auth variable at the top
const auth = getAuth();

/**
 * Connection states for Firestore
 */
export enum ConnectionState {
  UNKNOWN = 'unknown',
  ONLINE = 'online',
  OFFLINE = 'offline',
  AUTH_ERROR = 'auth_error',
  OTHER_ERROR = 'other_error'
}

/**
 * Interface for connection check result
 */
interface ConnectionCheckResult {
  success: boolean;
  state: ConnectionState;
  error?: Error;
}

/**
 * Check if we can connect to Firestore
 * @returns Promise<ConnectionCheckResult> Object with connection result
 */
export const checkFirestoreConnection = async (): Promise<ConnectionCheckResult> => {
  // First check if we have network connectivity
  if (!navigator.onLine) {
    return {
      success: false,
      state: ConnectionState.OFFLINE
    };
  }

  try {
    // Use printAgents collection since it's publicly readable
    const collRef = collection(db, 'printAgents');
    // Just try to get a query snapshot with limit 1 to verify connection works
    await getDocs(query(collRef, limit(1)));
    
    return {
      success: true,
      state: ConnectionState.ONLINE
    };
  } catch (error: any) {
    console.error('Firestore connection check failed:', error);
    
    // Determine the type of error
    if (
      error.code === 'permission-denied' || 
      error.code === 'unauthenticated' ||
      error.message?.includes('auth') ||
      error.message?.includes('permission') ||
      error.message?.includes('unauthorized')
    ) {
      return {
        success: false,
        state: ConnectionState.AUTH_ERROR,
        error: error instanceof Error ? error : new Error(error.message || 'Authentication error')
      };
    }
    
    return {
      success: false,
      state: ConnectionState.OTHER_ERROR,
      error: error instanceof Error ? error : new Error(error.message || 'Unknown connection error')
    };
  }
};

/**
 * Attempt to refresh Firebase authentication
 * @returns Promise<boolean> Success of refresh operation
 */
export const refreshFirebaseAuth = async (): Promise<boolean> => {
  try {
    // If we have a current user, try to refresh their token
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
      return true;
    } 
    // If no user, try anonymous sign in as fallback
    else {
      await signInAnonymously(auth);
      
      // Wait for auth state to change
      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(!!user);
        });
        
        // Timeout after 5 seconds
        setTimeout(() => {
          unsubscribe();
          resolve(false);
        }, 5000);
      });
    }
  } catch (error) {
    console.error('Failed to refresh authentication:', error);
    return false;
  }
};

/**
 * General network status monitoring
 */
export const onNetworkStatusChange = (callback: (isOnline: boolean) => void): () => void => {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Call it once immediately with current status
  callback(navigator.onLine);
  
  // Return a cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

/**
 * Monitor Firestore connection with callbacks for various states
 * @param onConnected Callback when connected
 * @param onDisconnected Callback when offline
 * @param onAuthError Callback when auth error occurs
 * @param onOtherError Callback when other error occurs
 * @param checkInterval Interval in ms to check connection (default: 60000)
 * @returns Function to stop monitoring
 */
export const monitorFirestoreConnection = (
  onConnected: () => void,
  onDisconnected: () => void,
  onAuthError: () => void,
  onOtherError: (error?: Error) => void,
  checkInterval: number = 60000
): (() => void) => {
  let isMonitoring = true;
  let timeoutId: ReturnType<typeof setTimeout>;
  
  const checkConnection = async () => {
    if (!isMonitoring) return;
    
    try {
      const connectionResult = await checkFirestoreConnection();
      
      if (connectionResult.success) {
        onConnected();
      } else {
        switch (connectionResult.state) {
          case ConnectionState.OFFLINE:
            onDisconnected();
            break;
          case ConnectionState.AUTH_ERROR:
            onAuthError();
            break;
          default:
            onOtherError(connectionResult.error);
            break;
        }
      }
    } catch (error: any) {
      onOtherError(error instanceof Error ? error : new Error(error?.message || 'Unknown error'));
    }
    
    // Schedule next check if still monitoring
    if (isMonitoring) {
      timeoutId = setTimeout(checkConnection, checkInterval);
    }
  };
  
  // Start initial check
  checkConnection();
  
  // Return function to stop monitoring
  return () => {
    isMonitoring = false;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
};

/**
 * Clear all app state/cache and reload
 */
export const reloadAppWithCleanState = (): void => {
  try {
    // Sign out user if logged in
    if (auth.currentUser) {
      auth.signOut().catch(console.error);
    }
    
    // Clear local storage
    localStorage.clear();
    
    // Clear session storage
    sessionStorage.clear();
    
    // Clear IndexedDB if supported
    if (window.indexedDB) {
      // Attempt to delete Firebase storage DBs
      const dbNames = [
        'firebaseLocalStorageDb',
        'firestore/[DEFAULT]',
        'firebase-installations-database',
        'firebase-heartbeat-database'
      ];
      
      dbNames.forEach(dbName => {
        try {
          window.indexedDB.deleteDatabase(dbName);
        } catch (e) {
          console.error(`Failed to delete IndexedDB '${dbName}':`, e);
        }
      });
    }
    
    // Wait a moment then reload
    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
  } catch (error) {
    console.error('Error during clean reload:', error);
    // Force reload even if errors occur
    window.location.reload();
  }
};

/**
 * Utility to check if app is in offline mode
 * @returns boolean indicating if app is offline
 */
export const isOffline = (): boolean => {
  return !navigator.onLine;
}; 